import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../db';
import { getEarnedBadges, getLockedBadges } from '../utils/badgeUtils';
import { Trophy, Lock, Check, Sparkles, Award } from 'lucide-react';

// Extraction propre du titre et de l'émoji du badge
const parseBadgeName = (fullName = '') => {
  const match = fullName.match(/(\p{Extended_Pictographic}.*)$/u);
  if (match) {
    return {
      title: fullName.slice(0, match.index).trim(),
      icon: match[1].trim()
    };
  }
  return { title: fullName, icon: '🏆' };
};

// Nettoyage et raccourcissement des descriptions pour un alignement parfait de toutes les cartes
const formatBadgeDescription = (b, isSecret) => {
  if (isSecret) return 'Condition secrète à découvrir';
  if (!b.description) return '';
  return b.description
    .replace("Pionnier ayant testé l'application en avant-première (réservé aux 30 premiers inscrits)", "Pionnier ayant testé l'app en avant-première")
    .replace("A enregistré une séance avec un ressenti ", "Séance avec ressenti ")
    .replace("A terminé sa première séance", "Première séance terminée")
    .replace("A terminé 10 séances d'entraînement", "10 séances complétées")
    .replace("A terminé 50 séances d'entraînement", "50 séances complétées")
    .replace("A terminé 100 séances d'entraînement", "100 séances complétées")
    .replace("A terminé une séance en moins de 20 minutes", "Séance en moins de 20 min")
    .replace("A fait une séance de plus de 2 heures", "Séance de plus de 2 heures")
    .replace("A fait au moins 3 séances dans une même semaine", "3 séances dans une même semaine")
    .replace("A fait 5 séances dans une même semaine", "5 séances dans une même semaine")
    .replace("A fait 15 séances en 30 jours", "15 séances en 30 jours")
    .replace("A fait les pecs un lundi", "Séance pecs un lundi")
    .replace("A fait plus de ", "Plus de ")
    .replace("A soulevé plus de ", "Plus de ")
    .replace("A soulevé ", "");
};

const getRankTitle = (percentage) => {
  if (percentage >= 80) return 'Légende Vivante';
  if (percentage >= 60) return 'Maître de la Fonte';
  if (percentage >= 40) return 'Vétéran de la Salle';
  if (percentage >= 20) return 'Chasseur de Trophées';
  return 'Novice Motivé';
};

const Badges = ({ currentProfile }) => {
  const [earned, setEarned] = useState([]);
  const [locked, setLocked] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtre de statut uniquement : 'all' | 'earned' | 'locked'
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const loadBadges = async () => {
      if (!currentProfile) return;
      const workouts = await db.getWorkouts(currentProfile.id);
      const goals = await db.getGoals(currentProfile.id);
      
      setEarned(getEarnedBadges(workouts, goals, currentProfile));
      setLocked(getLockedBadges(workouts, goals, currentProfile));
      setLoading(false);
    };

    loadBadges();
  }, [currentProfile]);

  const totalBadges = earned.length + locked.length;
  const pct = totalBadges > 0 ? Math.round((earned.length / totalBadges) * 100) : 0;

  // Filtrage selon le statut sélectionné
  const filteredBadges = useMemo(() => {
    const earnedAnnotated = earned.map(b => ({ ...b, isUnlocked: true }));
    const lockedAnnotated = locked.map(b => ({ ...b, isUnlocked: false }));

    if (statusFilter === 'earned') return earnedAnnotated;
    if (statusFilter === 'locked') return lockedAnnotated;
    return [...earnedAnnotated, ...lockedAnnotated];
  }, [earned, locked, statusFilter]);

  if (loading) {
    return (
      <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Chargement de tes succès...
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '3rem', width: '100%', maxWidth: '1000px', margin: '0 auto' }}>
      <style>{`
        .badge-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 0.9rem;
        }
        @media (max-width: 480px) {
          .badge-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.65rem;
          }
        }
        .badge-card {
          height: 195px;
          box-sizing: border-box;
          user-select: none;
        }
        .filter-pill {
          transition: all 0.2s ease;
        }
        .filter-pill:hover {
          filter: brightness(1.15);
        }
      `}</style>

      {/* TITRE DE LA PAGE */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '0.6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '12px',
            backgroundColor: 'rgba(140, 127, 191, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--primary-color)'
          }}>
            <Trophy size={24} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-primary)' }}>Mes Succès</h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Récompenses et faits d'armes d'entraînement</div>
          </div>
        </div>

        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.35rem 0.8rem',
          borderRadius: '12px',
          backgroundColor: 'rgba(140, 127, 191, 0.1)',
          border: '1px solid rgba(140, 127, 191, 0.2)',
          fontSize: '0.8rem',
          fontWeight: 700,
          color: 'var(--primary-color-hover)'
        }}>
          <span>🎖️ Rang :</span>
          <span style={{ color: '#fbbf24' }}>{getRankTitle(pct)}</span>
        </div>
      </div>

      {/* HERO BANNER : PROGRESSION GLOBALE (% COMPLÉTÉ) */}
      <div style={{
        backgroundColor: 'var(--bg-card, #1c1a24)',
        border: '1.5px solid rgba(140, 127, 191, 0.22)',
        borderRadius: '20px',
        padding: '1.4rem',
        marginBottom: '1.4rem',
        boxShadow: 'var(--shadow)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Glow discret d'ambiance aux couleurs du site */}
        <div style={{
          position: 'absolute',
          top: '-30%',
          right: '-5%',
          width: '260px',
          height: '260px',
          background: 'radial-gradient(circle, rgba(140, 127, 191, 0.18) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '54px', height: '54px', borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(140, 127, 191, 0.25) 100%)',
              border: '1px solid rgba(251, 191, 36, 0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(251, 191, 36, 0.18)'
            }}>
              <Award size={28} color="#fbbf24" />
            </div>
            <div>
              <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                {earned.length} <span style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 600 }}>/ {totalBadges}</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {earned.length === totalBadges ? 'Tous les succès débloqués ! Félicitations !' : `${totalBadges - earned.length} succès restants à décrocher`}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 900, color: 'var(--primary-color-hover)', lineHeight: 1 }}>
              {pct}%
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '0.2rem' }}>
              Complété
            </div>
          </div>
        </div>

        {/* BARRE DE PROGRESSION */}
        <div style={{
          marginTop: '1.2rem',
          width: '100%',
          height: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '10px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg, var(--primary-color, #8c7fbf) 0%, var(--secondary-color, #6eb583) 100%)',
            borderRadius: '10px',
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 0 12px rgba(110, 181, 131, 0.4)'
          }} />
        </div>
      </div>

      {/* FILTRES PAR STATUT UNIQUEMENT (TOUS, DÉBLOQUÉS, À FAIRE) */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.4rem' }}>
        <div style={{
          display: 'inline-flex',
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid var(--border-color, #2f2c3d)',
          borderRadius: '14px',
          padding: '0.25rem',
          gap: '0.3rem'
        }}>
          {[
            { id: 'all', label: `Tous (${totalBadges})` },
            { id: 'earned', label: `Débloqués (${earned.length})` },
            { id: 'locked', label: `À faire (${locked.length})` }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className="filter-pill"
              style={{
                border: 'none',
                outline: 'none',
                cursor: 'pointer',
                padding: '0.45rem 1rem',
                borderRadius: '11px',
                fontSize: '0.82rem',
                fontWeight: statusFilter === tab.id ? 800 : 600,
                backgroundColor: statusFilter === tab.id ? 'var(--primary-color, #8c7fbf)' : 'transparent',
                color: statusFilter === tab.id ? '#ffffff' : 'var(--text-secondary)'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* GRILLE DES SUCCÈS (HAUTEUR FIXE ET UNIFORME, SANS POPUP) */}
      {filteredBadges.length === 0 ? (
        <div style={{
          backgroundColor: 'var(--bg-card, #1c1a24)',
          border: '1px dashed var(--border-color, #2f2c3d)',
          borderRadius: '20px',
          padding: '3rem 1.5rem',
          textAlign: 'center',
          color: 'var(--text-secondary)'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.6rem' }}>🏆</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.3rem' }}>
            Aucun succès dans cette section
          </div>
          <div style={{ fontSize: '0.85rem' }}>
            {statusFilter === 'earned' 
              ? 'Entraîne-toi pour décrocher ton premier succès !'
              : 'Félicitations, tu as terminé tous les succès !'}
          </div>
        </div>
      ) : (
        <div className="badge-grid">
          {filteredBadges.map(b => {
            const isBeta = b.id === 'beta_tester';
            const isBoss = b.id.startsWith('boss_slayer');
            const isSecret = b.secret && !b.isUnlocked;
            const parsed = parseBadgeName(b.name);
            const shortDesc = formatBadgeDescription(b, isSecret);

            // Couleurs basées strictement sur la charte du site
            let borderColor = 'var(--border-color, #2f2c3d)';
            let bgGradient = 'rgba(22, 20, 31, 0.55)';
            let shadow = 'none';

            if (b.isUnlocked) {
              if (isBeta || isBoss) {
                borderColor = 'rgba(251, 191, 36, 0.45)';
                bgGradient = 'linear-gradient(160deg, rgba(36, 28, 48, 0.95) 0%, rgba(24, 18, 34, 0.98) 100%)';
                shadow = '0 6px 20px rgba(251, 191, 36, 0.12), 0 2px 8px rgba(0,0,0,0.3)';
              } else {
                borderColor = 'rgba(140, 127, 191, 0.32)';
                bgGradient = 'linear-gradient(160deg, rgba(30, 27, 43, 0.95) 0%, rgba(20, 18, 30, 0.98) 100%)';
                shadow = '0 6px 20px rgba(140, 127, 191, 0.12), 0 2px 8px rgba(0,0,0,0.3)';
              }
            } else if (isSecret) {
              // Secret aux couleurs sobres du site (pastel purple / border subtile)
              borderColor = 'rgba(140, 127, 191, 0.25)';
              bgGradient = 'rgba(22, 20, 31, 0.55)';
            }

            return (
              <div 
                key={b.id}
                className="badge-card"
                style={{
                  background: bgGradient,
                  borderRadius: '18px',
                  padding: '1rem 0.75rem 0.85rem 0.75rem',
                  textAlign: 'center',
                  border: `1.5px ${b.isUnlocked ? 'solid' : 'dashed'} ${borderColor}`,
                  boxShadow: shadow,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* RIBBON / BADGE EN HAUT À DROITE */}
                {b.isUnlocked && (isBeta || isBoss) && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: isBoss ? '#8c7fbf' : '#fbbf24',
                    color: isBoss ? '#ffffff' : '#1e1b26',
                    fontSize: '0.55rem',
                    padding: '0.12rem 0.4rem',
                    borderBottomLeftRadius: '7px',
                    fontWeight: 900,
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase'
                  }}>
                    {isBeta ? 'Pionnier' : 'Raid Boss'}
                  </div>
                )}

                {/* MÉDAILLON / ICÔNE */}
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: b.isUnlocked
                    ? (isBeta || isBoss
                        ? 'radial-gradient(circle at 35% 35%, rgba(251, 191, 36, 0.28) 0%, rgba(140, 127, 191, 0.15) 70%)'
                        : 'radial-gradient(circle at 35% 35%, rgba(140, 127, 191, 0.28) 0%, rgba(110, 181, 131, 0.12) 70%)')
                    : (isSecret 
                        ? 'rgba(140, 127, 191, 0.08)'
                        : 'rgba(255, 255, 255, 0.02)'),
                  border: b.isUnlocked
                    ? (isBeta || isBoss ? '1.5px solid rgba(251, 191, 36, 0.5)' : '1.5px solid rgba(140, 127, 191, 0.4)')
                    : (isSecret ? '1.5px dashed rgba(140, 127, 191, 0.3)' : '1.5px dashed rgba(255, 255, 255, 0.12)'),
                  boxShadow: b.isUnlocked
                    ? (isBeta || isBoss ? '0 0 16px rgba(251, 191, 36, 0.2)' : '0 0 14px rgba(140, 127, 191, 0.18)')
                    : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  flexShrink: 0
                }}>
                  {b.isUnlocked ? (
                    <span style={{ fontSize: '28px', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' }}>
                      {parsed.icon}
                    </span>
                  ) : isSecret ? (
                    <span style={{ fontSize: '24px', fontWeight: 900, color: 'var(--primary-color, #8c7fbf)' }}>
                      ?
                    </span>
                  ) : (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '26px', opacity: 0.2, filter: 'grayscale(1)' }}>
                        {parsed.icon}
                      </span>
                      <div style={{
                        position: 'absolute',
                        backgroundColor: 'rgba(18, 16, 22, 0.85)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-secondary)'
                      }}>
                        <Lock size={11} />
                      </div>
                    </div>
                  )}
                </div>

                {/* TITRE ET EXPLICATION (HAUTEURS FIXES POUR CARTES 100% UNIFORMES) */}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                  {/* Titre */}
                  <div style={{
                    height: '2.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.84rem',
                    fontWeight: 800,
                    color: b.isUnlocked ? 'var(--text-primary)' : 'var(--text-secondary)',
                    lineHeight: 1.2,
                    textAlign: 'center'
                  }}>
                    <span style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {isSecret ? 'Succès Secret' : parsed.title}
                    </span>
                  </div>

                  {/* Description courte */}
                  <div style={{
                    height: '2.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.72rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.25,
                    textAlign: 'center'
                  }}>
                    <span style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {shortDesc}
                    </span>
                  </div>
                </div>

                {/* PASTILLE D'ÉTAT EN BAS */}
                <div style={{ height: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {b.isUnlocked ? (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.18rem 0.5rem',
                      borderRadius: '9px',
                      backgroundColor: isBeta || isBoss ? 'rgba(251, 191, 36, 0.14)' : 'rgba(110, 181, 131, 0.14)',
                      color: isBeta || isBoss ? '#fbbf24' : 'var(--secondary-color)',
                      border: isBeta || isBoss ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(110, 181, 131, 0.25)',
                      fontSize: '0.67rem',
                      fontWeight: 700
                    }}>
                      <Check size={10} strokeWidth={3} /> Obtenu
                    </span>
                  ) : isSecret ? (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.18rem 0.5rem',
                      borderRadius: '9px',
                      backgroundColor: 'rgba(140, 127, 191, 0.08)',
                      color: 'var(--primary-color-hover, #a89fcf)',
                      border: '1px solid rgba(140, 127, 191, 0.22)',
                      fontSize: '0.67rem',
                      fontWeight: 600
                    }}>
                      <Sparkles size={10} /> Secret
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.18rem 0.5rem',
                      borderRadius: '9px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      color: 'var(--text-secondary)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      fontSize: '0.67rem',
                      fontWeight: 600
                    }}>
                      <Lock size={10} /> À débloquer
                    </span>
                  )}
                </div>

              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default Badges;
